/* eslint-disable camelcase */
import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Divider,
  Icons,
  InputAdornment,
} from '@nextiva/next-ui-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getProfileIdsLicense } from 'api/licenseService';
import {
  StyledSearchTextField,
  StyledSearchIcon,
  StyledClearIcon,
} from 'components/SearchBar';
import {
  LicensesContext,
  actionTypes,
} from 'containers/pages/BillingLicenses/licensesContext';
import { useSnackbar } from 'hooks';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

import { UserChips } from './UserChips';
import { List } from './List';
import LicenseCounts from './LicenseCounts';
import DrawerFooter from './DrawerFooter';
import { PurchaseLicensesBanner } from './PurchaseLicensesBanner/PurchaseLicensesBanner.tsx';

const EditAssignment = ({ toggleDrawer }) => {
  const {
    dispatch,
    licensesState: {
      licensesUserList,
      selectedUserList,
      editLicenseAssignment: {
        name,
        id: licenseId,
        license_count,
        licenseCount,
        license_definition,
        licenseDefinition,
      },
    },
  } = useContext(LicensesContext);

  const {
    featureState: { authV3Enabled, purchaseLicenseFlow },
    corpAcctNbr,
  } = useContext(FeatureContext);
  const purchaseLicenseFlowGrantStatus = useSelector(
    (state) =>
      state.globalPolicyDecisionRequestState.purchaseLicenseFlowGrantStatus,
  );
  const accountV2Summary = useSelector(
    (state) => state.globalAccountV2SummaryState.globalAccountV2Summary,
  );
  const inAppPurchaseEnabled =
    accountV2Summary?.attributes?.inAppPurchaseEnabled === 'true' ||
    accountV2Summary?.attributes?.inAppPurchaseEnabled === true;
  const freemiumFlowEnabled =
    accountV2Summary?.attributes?.freemium === 'true' ||
    accountV2Summary?.attributes?.freemium === true;

  const isPurchaseLicenseFlowEnabled =
    purchaseLicenseFlow &&
    inAppPurchaseEnabled &&
    purchaseLicenseFlowGrantStatus &&
    !freemiumFlowEnabled;

  const [searchDataValue, setSearchDataValue] = useState('');

  const setSnackbar = useSnackbar(true);

  useEffect(() => {
    if (selectedUserList.length === 0) {
      getProfileIdsLicense(licenseId, authV3Enabled, corpAcctNbr)
        .then((profileIds) => {
          dispatch({
            type: actionTypes.SET_SELECTED_USER_LIST,
            profileIds,
            authV3Enabled,
          });
        })
        .catch(() => {
          setSnackbar({
            message: `Couldn't get users associated to this license.`,
            severity: 'error',
          });
        });
    }
  }, [licensesUserList]);

  const searchData = (e) => {
    setSearchDataValue(e.target.value);
    dispatch({
      type: actionTypes.FILTER_USER_LIST_BY_SEARCH,
      searchString: e.target.value,
    });
  };

  return (
    <div className="m-edit-assignment">
      {isPurchaseLicenseFlowEnabled && (
        <PurchaseLicensesBanner toggleDrawer={toggleDrawer} />
      )}
      {(selectedUserList.length === license_count ||
        selectedUserList.length === licenseCount) &&
        !isPurchaseLicenseFlowEnabled && (
          <div
            className="warning-banner"
            data-testid="PlatformAdminUI-Licensing-EditAssignment-WarningBanner"
          >
            <Alert fullWidth severity="warning">
              Looks like you have used all of your licenses. If you would like
              to add more please contact us at{' '}
              <u>
                <a
                  href="tel:18009834289"
                  data-testid="PlatformAdminUI-Licensing-EditAssignment-Number"
                >
                  1-800-983-4289
                </a>
              </u>
              .
            </Alert>
          </div>
        )}
      <div
        className="m-edit-assignment__name"
        data-testid="PlatformAdminUI-Licensing-EditAssignment-Name"
      >
        {name.includes('NUC-')
          ? license_definition?.description || licenseDefinition?.description
          : name}
      </div>
      <LicenseCounts />
      <Divider sx={{ mb: '24px' }} />
      <UserChips />
      <div className="m-create-team__search">
        <div
          className="Heading3 m-create-team__search__title"
          data-testid="PlatformAdminUI-Licensing-EditAssignment-SearchTitle"
        >
          Search and select users for this license
        </div>
        <div className="m-create-team__search__search">
          <StyledSearchTextField
            hiddenLabel
            id="search"
            value={searchDataValue}
            fullWidth
            placeholder="Search users"
            onChange={searchData}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon icon={Icons.search} />
                </InputAdornment>
              ),
              endAdornment: searchDataValue && (
                <InputAdornment position="end">
                  <StyledClearIcon
                    icon={Icons.close}
                    onClick={() => setSearchDataValue('')}
                    data-testid="PlatformAdminUI-Licensing-EditAssignment-SearchField-Clear"
                  />
                </InputAdornment>
              ),
            }}
            data-testid="PlatformAdminUI-Licensing-EditAssignment-SearchField"
          />
        </div>
      </div>
      {(selectedUserList.length > license_count ||
        selectedUserList.length > licenseCount) && (
        <div className="warning-banner">
          <Alert fullWidth severity="warning">
            The available number of users that can be licensed is{' '}
            <b>
              {license_count || licenseCount} user
              {(license_count > 1 || licenseCount > 1) && 's'}
            </b>
            . Please edit which users are selected or remove users that are
            currently assigned.
          </Alert>
        </div>
      )}
      <List />
      <DrawerFooter toggleDrawer={toggleDrawer} />
    </div>
  );
};

EditAssignment.propTypes = {
  toggleDrawer: PropTypes.func.isRequired,
};

export default EditAssignment;
